import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    viewChildren,
} from '@angular/core';
import {FormArray, FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
    composeComparators,
    filterNulls,
    fromControl,
    includesIgnoreCase,
    keyComparator,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {
    NgbActiveModal,
    NgbDropdown,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbPopover,
} from '@ng-bootstrap/ng-bootstrap';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {BehaviorSubject, combineLatest, map, Observable, switchMap, tap} from 'rxjs';
import {
    ColumnHeader,
    FilterForm,
    FilterFormGroup,
    MeterFormGroup,
    SelectableLocationMeterFormGroup,
    SelectFormGroup,
} from './select-meters-for-variable-alarms.model';
import {SelectMetersForVariableAlarmValidator} from './select-meter-validator';
import {getColumnHeads} from './variable-alarm-column-heads';
import {MonitoringMeterWithTranslations} from '../../../../monitoring-meter.model';
import {
    CheckboxGroupComponent,
    ControlMessagesComponent,
    DatePipe,
    PaginationComponent,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    Sorting,
} from '@brunata-angular/ui';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-select-meters-for-variable-alarms',
    standalone: true,
    imports: [
        TranslatePipe,
        CommonModule,
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        InputDateGroupComponent,
        CheckboxGroupComponent,
        PaginationComponent,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        DatePipe,
        NgbPopover,
        ControlMessagesComponent,
        SortableTableHeaderComponent,
    ],
    templateUrl: './select-meters-for-variable-alarms.component.html',
    styleUrls: ['./select-meters-for-variable-alarms.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectMetersForVariableAlarmsComponent implements OnInit {
    private readonly headers = viewChildren(SortableTableHeaderComponent);
    protected sorting = new BehaviorSubject<Sorting>({column: '', direction: 'asc'});

    @Input() usageAndUnit: string | null = null;
    @Input() meterIds: FormArray<FormControl<string>> = new FormArray<FormControl<string>>([]);
    @Input() fromTemplate = false;
    private readonly metersForAgentsSubject = new BehaviorSubject<
        MonitoringMeterWithTranslations[]
    >([]);

    @Input() set metersForAgents(meters: MonitoringMeterWithTranslations[]) {
        this.metersForAgentsSubject.next(meters);
    }

    @Output() submitSelectedMeters = new EventEmitter<string[]>();
    protected readonly activeModal = inject(NgbActiveModal);
    private readonly destroyRef = inject(DestroyRef);
    private readonly nonNullableFormBuilder = inject(NonNullableFormBuilder);
    private readonly translateService = inject(TranslateService);
    protected page = 1;
    protected pageSize = 10;

    protected selectedColumnsForm: SelectFormGroup = this.nonNullableFormBuilder.group({
        select: [true],
        meterNo: [true],
        printedSerialNo: [false],
        meterModel: [false],
        placement: [false],
        usageAndUnit: [true],
        mountingDate: [true],
        dismountedDate: [false],
        address: [false],
        property: [false],
        branch: [false],
        location: [false],
        currentResident: [false],
    });
    protected selectAll: FormControl<boolean> = this.nonNullableFormBuilder.control<boolean>(true);
    protected filterForm: FilterFormGroup = this.nonNullableFormBuilder.group({
        select: this.nonNullableFormBuilder.control<SelectableItem[]>([
            {name: 'selected', selected: false},
            {name: 'unselected', selected: false},
        ]),
        meterNo: this.nonNullableFormBuilder.control<string>(''),
        printedSerialNo: this.nonNullableFormBuilder.control<string>(''),
        meterModel: this.nonNullableFormBuilder.control<SelectableItem[]>([]),
        placement: this.nonNullableFormBuilder.control<string>(''),
        usageAndUnit: this.nonNullableFormBuilder.control<SelectableItem[]>([]),
        mountingDate: this.nonNullableFormBuilder.control<DateTime | null>(null),
        dismountedDate: this.nonNullableFormBuilder.control<DateTime | null>(null),
        address: this.nonNullableFormBuilder.control<string>(''),
        property: this.nonNullableFormBuilder.control<string>(''),
        branch: this.nonNullableFormBuilder.control<string>(''),
        location: this.nonNullableFormBuilder.control<string>(''),
        currentResident: this.nonNullableFormBuilder.control<string>(''),
        replacedBy: this.nonNullableFormBuilder.control<string>(''),
    });
    meterFormGroup: MeterFormGroup = this.nonNullableFormBuilder.group({
        meterFormArray: this.nonNullableFormBuilder.array<SelectableLocationMeterFormGroup>([]),
    });

    protected columnHeads: ColumnHeader[] = getColumnHeads(
        this.filterForm,
        this.translateService,
        this.selectedColumnsForm,
    );

    private readonly meterData$: Observable<MonitoringMeterWithTranslations[]> =
        this.metersForAgentsSubject.pipe(
            filterNulls(),
            map(meters =>
                meters.filter(
                    meter =>
                        (meter.usageAndUnit === this.usageAndUnit ||
                            this.meterIds.value.find(meterId => meterId === meter.meterId)) ===
                        true,
                ),
            ),
            tap(meters => this.patchForms(meters)),
        );

    protected readonly filteredMetersFormGroup$: Observable<MeterFormGroup> = this.meterData$.pipe(
        switchMap(() =>
            combineLatest([fromControl(this.filterForm), this.sorting]).pipe(
                tap(() => (this.page = 1)),
                map(([filterForm, sorting]) => {
                    const meterFormGroup: MeterFormGroup = this.nonNullableFormBuilder.group({
                        meterFormArray: new FormArray(
                            this.filterFormArray(
                                {
                                    select: filterForm.select ?? [],
                                    meterNo: filterForm.meterNo ?? '',
                                    printedSerialNo: filterForm.printedSerialNo ?? '',
                                    meterModel: filterForm.meterModel ?? [],
                                    placement: filterForm.placement ?? '',
                                    usageAndUnit: filterForm.usageAndUnit ?? [],
                                    mountingDate: filterForm.mountingDate ?? undefined,
                                    dismountedDate: filterForm.dismountedDate ?? undefined,
                                    address: filterForm.address ?? '',
                                    property: filterForm.property ?? '',
                                    branch: filterForm.branch ?? '',
                                    location: filterForm.location ?? '',
                                    currentResident: filterForm.currentResident ?? '',
                                    replacedBy: filterForm.replacedBy ?? '',
                                },
                                this.meterFormGroup.controls.meterFormArray,
                            ),
                            SelectMetersForVariableAlarmValidator.meterUsageAndUnitValidator(
                                this.usageAndUnit ?? '',
                            ),
                        ),
                    });

                    this.sortMeters(meterFormGroup, sorting);
                    return meterFormGroup;
                }),
            ),
        ),
    );

    protected readonly view$ = this.filteredMetersFormGroup$.pipe(
        map(filteredMetersFormGroup => filteredMetersFormGroup),
    );

    ngOnInit(): void {
        fromControl(this.meterFormGroup.controls.meterFormArray)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(value => this.selectAll.setValue(value.every(meter => meter.select === true))),
            )
            .subscribe();
    }

    private filterFormArray(
        filterForm: FilterForm,
        formArray: FormArray<SelectableLocationMeterFormGroup>,
    ): SelectableLocationMeterFormGroup[] {
        return formArray.controls.filter(
            meter =>
                (filterForm.select.every(it => !it.selected) ||
                    ((filterForm.select.find(it => it.name === 'selected')?.selected ?? false) &&
                        meter.getRawValue().select) ||
                    ((filterForm.select.find(it => it.name === 'unselected')?.selected ?? false) &&
                        !meter.getRawValue().select)) &&
                includesIgnoreCase(meter.value.meterNo, filterForm.meterNo) &&
                includesIgnoreCase(meter.value.placement, filterForm.placement) &&
                this.filteredArrayValues(meter.value.usageAndUnit ?? '', filterForm.usageAndUnit) &&
                this.filteredArrayValues(meter.value.meterModel ?? '', filterForm.meterModel) &&
                includesIgnoreCase(
                    meter.value.mountingDate?.toString(),
                    filterForm.mountingDate?.toString(),
                ) &&
                includesIgnoreCase(
                    meter.value.dismountedDate?.toString(),
                    filterForm.dismountedDate?.toString(),
                ) &&
                includesIgnoreCase(meter.value.address, filterForm.address) &&
                includesIgnoreCase(meter.value.property, filterForm.property) &&
                includesIgnoreCase(meter.value.branch, filterForm.branch) &&
                includesIgnoreCase(meter.value.location, filterForm.location) &&
                includesIgnoreCase(meter.value.currentResident, filterForm.currentResident),
        );
    }

    private patchForms(meters: MonitoringMeterWithTranslations[]): void {
        this.meterFormGroup.controls.meterFormArray.clear();
        meters.forEach(meter => {
            const selectByUsageAndUnit =
                this.usageAndUnit != null && this.usageAndUnit === meter.usageAndUnit;
            const selectByMeterId =
                this.meterIds.length > 0
                    ? this.meterIds.value.find(meterId => meterId === meter.meterId) != null
                    : selectByUsageAndUnit;

            const formGroup: SelectableLocationMeterFormGroup = this.nonNullableFormBuilder.group({
                select: this.nonNullableFormBuilder.control<boolean>(selectByMeterId),
                meterId: this.nonNullableFormBuilder.control<string>(meter.meterId),
                meterNo: this.nonNullableFormBuilder.control<string>(meter.meterNo),
                printedSerialNo: this.nonNullableFormBuilder.control<string>(
                    meter.printedSerialNo ?? '',
                ),
                meterModel: this.nonNullableFormBuilder.control<string>(meter.meterModel),
                placement: this.nonNullableFormBuilder.control<string>(meter.placement),
                usageAndUnit: this.nonNullableFormBuilder.control<string>(meter.usageAndUnit),
                mountingDate: this.nonNullableFormBuilder.control<DateTime | null>(
                    meter.mountingDate ?? null,
                ),
                dismountedDate: this.nonNullableFormBuilder.control<DateTime | null>(
                    meter.dismountedDate ?? null,
                ),
                address: this.nonNullableFormBuilder.control<string>(meter.address),
                property: this.nonNullableFormBuilder.control<string>(meter.propertyNo),
                branch: this.nonNullableFormBuilder.control<string>(meter.branchNo),
                location: this.nonNullableFormBuilder.control<string>(meter.locationNo),
                currentResident: this.nonNullableFormBuilder.control<string>(meter.consumerName),
                replacedBy: this.nonNullableFormBuilder.control<string | null>(
                    meter.replacedBy ?? null,
                ),
                meterCriteriaMismatch: this.nonNullableFormBuilder.control<boolean>(
                    meter.meterCriteriaMismatch,
                ),
            });

            this.meterFormGroup.controls.meterFormArray.push(formGroup);
        });

        const selectableUsageAndUnits: SelectableItem[] = this.getUniqueValuesForLists(
            meters,
            'usageAndUnit',
        );
        if (
            this.usageAndUnit != null &&
            !selectableUsageAndUnits.some(usageAndUnit => usageAndUnit.name === this.usageAndUnit)
        ) {
            selectableUsageAndUnits.push({
                name: this.usageAndUnit,
                selected: false,
            });
        }

        this.filterForm.patchValue({
            meterModel: this.getUniqueValuesForLists(meters, 'meterModel'),
            usageAndUnit: selectableUsageAndUnits,
        });
    }

    protected checkToggleSelectAll(meterFormGroup: MeterFormGroup): void {
        const allSelected = meterFormGroup
            .getRawValue()
            .meterFormArray.every(meter => meter.select);
        this.selectAll.setValue(allSelected);
    }

    protected toggleSelectAll(meterFormGroup: MeterFormGroup): MeterFormGroup {
        const allSelected = meterFormGroup
            .getRawValue()
            .meterFormArray.every(meter => meter.select);
        meterFormGroup.controls.meterFormArray.controls.forEach(control =>
            control.patchValue({select: !allSelected}),
        );
        return meterFormGroup;
    }

    protected submit(meterFormArray: FormArray<SelectableLocationMeterFormGroup>): void {
        meterFormArray.markAllAsTouched();
        if (meterFormArray.invalid) {
            return;
        }
        const selectedMeters = meterFormArray
            .getRawValue()
            .filter(meter => meter.select)
            .map(meter => meter.meterId);
        this.submitSelectedMeters.emit(selectedMeters);
        this.activeModal.close();
    }

    protected setPage(page: number): void {
        this.page = page;
    }

    protected resetColumns(): void {
        this.selectedColumnsForm.patchValue({
            select: true,
            meterNo: true,
            printedSerialNo: false,
            meterModel: false,
            placement: false,
            usageAndUnit: true,
            mountingDate: true,
            dismountedDate: false,
            address: false,
            property: false,
            branch: false,
            location: false,
            currentResident: false,
        });
    }

    private getUniqueValuesForLists(
        meters: MonitoringMeterWithTranslations[],
        key: keyof MonitoringMeterWithTranslations,
    ): SelectableItem[] {
        const uniqueNames = new Set<string>();
        const selectableItems: SelectableItem[] = [];

        meters.forEach(meter => {
            const value = meter[key];
            if (value != null) {
                const valueStr = value instanceof DateTime ? value.toISO() : value.toString();
                if (!uniqueNames.has(valueStr)) {
                    uniqueNames.add(valueStr);
                    selectableItems.push({
                        name: valueStr,
                        selected: false,
                    });
                }
            }
        });

        return selectableItems.sort((a, b) => a.name.localeCompare(b.name));
    }

    private filteredArrayValues(value: string, arrayFilterValue: SelectableItem[]): boolean {
        const valuesToFilter = arrayFilterValue.filter(it => it.selected).map(it => it.name);
        return valuesToFilter.length === 0 || valuesToFilter.includes(value);
    }

    protected updateSorting(sorting: Sorting): void {
        this.sorting.next(sorting);
    }

    protected onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.sorting.next(sorting);
    }

    private sortMeters(meters: MeterFormGroup, sorting: Sorting): MeterFormGroup {
        switch (sorting.column) {
            case '': {
                meters.controls.meterFormArray.setValue(
                    meters.controls.meterFormArray.controls
                        .sort(
                            composeComparators([
                                keyComparator(
                                    meter => meter.getRawValue().meterCriteriaMismatch,
                                    'desc',
                                ),
                                keyComparator(meter => meter.getRawValue().replacedBy, 'desc'),
                                keyComparator(meter => meter.getRawValue().property, 'asc'),
                                keyComparator(meter => meter.getRawValue().branch, 'asc'),
                                keyComparator(meter => meter.getRawValue().location, 'asc'),
                                keyComparator(meter => meter.getRawValue().meterNo, 'asc'),
                                keyComparator(meter => meter.getRawValue().mountingDate, 'asc'),
                            ]),
                        )
                        .map(control => control.getRawValue()),
                );
                break;
            }
            default: {
                meters.controls.meterFormArray.setValue(
                    meters.controls.meterFormArray.controls
                        .sort(
                            keyComparator(
                                meter =>
                                    meter.contains(sorting.column)
                                        ? meter.get(sorting.column)
                                        : null,
                                sorting.direction,
                            ),
                        )
                        .map(control => control.getRawValue()),
                );
                break;
            }
        }
        return meters;
    }
}
